import { useState, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { QueryParams, UseEntitySearchProps, UseEntitySearchReturn } from 'types';

export function useEntitySearch<T, R>({
  searchFunction,
  formatResults,
  filterResults = () => true,
  debounceTime = 300,
  defaultFilter = 'isActive:eq:true',
  defaultSort = 'name:asc',
  defaultFieldSearch = 'name',
  pageSize = 20,
  defaultInclude = '',
}: UseEntitySearchProps<T, R>): UseEntitySearchReturn<R> {
  const [searchResults, setSearchResults] = useState<R[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string | undefined>(undefined);

  const debouncedSearch = useDebouncedCallback((term: string, entityId?: string) => {
    setIsSearching(true);
    if (entityId !== undefined) {
      setCurrentId(entityId);
    }

    let filter = defaultFilter;
    if (term) {
      filter = `${defaultFieldSearch}:like:%${term}%&&${defaultFilter}`;
    }

    const searchQuery: QueryParams = {
      include: defaultInclude,
      page: 1,
      size: pageSize,
      filter,
      sort: defaultSort,
    };

    searchFunction({ query: searchQuery })
      .then((response: T) => {
        const formattedData = formatResults(response);
        const filteredData = formattedData.filter((item) => filterResults(item, currentId));
        setSearchResults(filteredData);
      })
      .catch((error: unknown) => {
        console.error('Search error:', error);
        setSearchResults([]);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, debounceTime);

  const handleSearch = useCallback(
    (term: string, entityId?: string) => {
      setSearchTerm(term);
      debouncedSearch(term, entityId);
    },
    [debouncedSearch],
  );

  const resetSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setCurrentId(undefined);
    debouncedSearch('');
  }, [debouncedSearch]);

  return {
    searchResults,
    isSearching,
    searchTerm,
    handleSearch,
    resetSearch,
  };
}
